import { IS_AUTO_TEST_HIDE, IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { overquota256TariffId, preOverquotaBanner, preQuotaOfferLimit } from 'reactApp/appHelpers/featuresHelpers/features/overquotaPre';
import { sendDwhModal } from 'reactApp/modules/dwh/dwh.helpers';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { loadProductsError, loadProductsSuccess, updateProducts } from 'reactApp/modules/products/products.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getProfileLoadingState, getUserOverquotaCycleCount } from 'reactApp/modules/profile/profile.selectors';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { addPromoToShowQueue, promoShown, removePromo } from 'reactApp/modules/promo/promo.module';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserStorageActions } from 'reactApp/modules/user/userStorage';
import { loadUserQuotaSuccess } from 'reactApp/modules/userQuota/userQuota.module';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { getQuotaPromoUrl } from 'reactApp/sections/QuotaLanding/QuotaLanding.data';
import { store as reduxStore } from 'reactApp/store';
import { renderOverquotaBanner } from 'reactApp/ui/OverquotaPopup/OverquotaPopup.helpers';
import { put, take } from 'redux-saga/effects';
import { call, select } from 'typed-redux-saga';

const PROMO_ID = 'preoverquota-promo';
const PERIOD_IN_DAYS = 3;

const name = 'mnz_avoid_overquota';

function markPromoShown() {
    reduxStore.dispatch(promoShown(EPromoType.preOverquotaPromo));
    storeHelper.markAsShown(PROMO_ID);
}

export function* initPreOverquotaPromo() {
    const storage = yield* select(getCurrentStorage);
    const { isHome } = getStorage(storage);
    const isBizUser = yield* select(UserSelectors.isBizUser);
    const isPaidUser = yield* select(UserSelectors.isPaidUser);

    // Форс-включение промки для отдельных автотестов.
    const force = yield put(UserStorageActions.get(`force_${PROMO_ID}`)) || false;

    const periodPassed = storeHelper.getValue(PROMO_ID) && storeHelper.isPassed(PROMO_ID, { daysCount: PERIOD_IN_DAYS });

    if (periodPassed) {
        storeHelper.clearStore(PROMO_ID);
    }

    if (!preOverquotaBanner || !isHome || (IS_AUTO_TEST_HIDE && !force) || isBizUser || isPaidUser || storeHelper.getValue(PROMO_ID)) {
        return;
    }

    const isQuotaLoaded = yield* select(UserQuotaSelectors.isQuotaLoaded);
    if (!isQuotaLoaded) {
        yield take(loadUserQuotaSuccess);
    }

    const isOverquota = yield* select(UserQuotaSelectors.isOverquota);
    const { used: usedPercent } = yield* select(UserQuotaSelectors.getFilledPercents);

    if (isOverquota || usedPercent < preQuotaOfferLimit) {
        return;
    }

    yield put(
        addPromoToShowQueue({
            type: EPromoType.preOverquotaPromo,
            promoId: PROMO_ID,
            onShow: () => {
                markPromoShown();
                sendDwhModal({ name });
            },
            onClick: () => {
                reduxStore.dispatch(removePromo(EPromoType.preOverquotaPromo));
                sendDwhModal({ action: 'click', name });

                reduxStore.dispatch(
                    openTariffBuy({
                        productId: overquota256TariffId,
                        isMobile: IS_PHONE_BROWSER,
                        paySource: getPaySource(`avoid_overquota_modal`),
                    })
                );
            },
            onClose: () => {
                reduxStore.dispatch(removePromo(EPromoType.preOverquotaPromo));
                sendDwhModal({ action: 'close', name });
            },
            onDismiss: () => {
                reduxStore.dispatch(removePromo(EPromoType.preOverquotaPromo));
            },
            onSecondaryClick: () => {
                reduxStore.dispatch(removePromo(EPromoType.preOverquotaPromo));
                sendDwhModal({ action: 'click-tariff', name });

                window.open(
                    getQuotaPromoUrl({
                        query: 'utm_source=cloud&utm_campaign=avoid_overquota_modal&utm_medium=web_union_quota_landing',
                        hash: 'tariffs',
                    }),
                    '_blank',
                    'noopener noreferrer'
                );
            },
        })
    );
}

export function* showPreOverquotaPromo() {
    const promo = yield* select(PromoSelectors.getPromo(EPromoType.preOverquotaPromo));

    if (promo) {
        const status = yield* select(getProfileLoadingState);
        if (!status?.isLoaded) {
            return;
        }

        const count = yield* select(getUserOverquotaCycleCount);

        if (!count) {
            yield put(removePromo(EPromoType.preOverquotaPromo));
            return;
        }

        let product = yield* select(ProductsSelectors.getProductById, overquota256TariffId);

        if (!product) {
            yield put(updateProducts({ productId: overquota256TariffId }));

            yield take([loadProductsSuccess, loadProductsError]);

            product = yield* select(ProductsSelectors.getProductById, overquota256TariffId);
        }

        const { onShow, onClick, onClose, onSecondaryClick } = promo;

        yield* call(renderOverquotaBanner, {
            isPrequota: true,
            primaryButtonText: `Увеличить на ${product?.space?.value || '256 ГБ'}`,
            secondaryButtonText: 'Другие тарифы',
            onShow,
            onClick,
            onDismiss: onClose,
            onSecondaryClick,
        });
    }
}
